import React, { ReactElement, useRef, useEffect, useState, MouseEvent } from 'react';
import { useHistory } from 'react-router-dom';
import { RouteDefinitions } from 'routes';
import { ClinicUserFragment } from 'shared/types/graphql';
import { useAuth } from 'shared/providers/AuthProvider';
import Popover, { IPopoverRef } from 'shared/components/Popover';
import { SnoozePopover } from './SnoozePopover';
import { IOption } from 'shared/components/Dropdown';
import ChangeClinicPopover from './ChangeClinicPopover';
import { Icon, Text, Avatar, HStack, VStack, Box, Divider, Flex } from '@televet/kibble-ui';
import { useTracking } from 'shared/providers/TrackingProvider';
import { GeneralEvents } from 'shared/providers/TrackingProvider/EventNames';
import HasModule from 'shared/components/HasModule';
import { ModuleName } from '@televet/shared-types/productPackages';
import { show as openIntercomMessenger } from '@intercom/messenger-js-sdk';
import { useModularity } from 'shared/hooks/useModularity';

export interface IUserProfilePopoverProps {
  user: ClinicUserFragment | null;
  clinicOptions: IOption[];
  clinicLogo?: string | null;
  anchor: ReactElement;
  clinicName: string;
  onClinicSelected: (e: MouseEvent, option: IOption) => Promise<void>;
}

interface IUserProfilePopoverContent {
  optionText: string;
  dataTestId: string;
  mixPanelName?: string;
  onClick?: () => void;
}

const UserProfilePopoverContent = ({
  optionText,
  dataTestId,
  mixPanelName,
  ...rest
}: IUserProfilePopoverContent): JSX.Element => {
  return (
    <Flex
      data-testid={dataTestId}
      data-mixpanel-name={mixPanelName}
      p="8px 20px"
      height={9}
      width="100%"
      justifyContent="flex-start"
      {...rest}
      cursor="pointer"
      _hover={{
        bg: 'background.primaryAlpha.hover',
      }}
    >
      <Text size="sm">{optionText}</Text>
    </Flex>
  );
};

const UserProfilePopover = ({
  user,
  clinicOptions,
  anchor,
  clinicName,
  onClinicSelected,
  clinicLogo,
}: IUserProfilePopoverProps): JSX.Element => {
  const { firstName, lastName, profilePic } = user || {};
  const [count, setCounter] = useState(0);
  const { logout, isImpersonating } = useAuth();
  const history = useHistory();
  const popoverRef = useRef<IPopoverRef>();
  const isSnoozed = new Date(user?.userSetting?.snoozeNotificationsUntil) > new Date();
  const { trackEvent } = useTracking();
  const { hasModule } = useModularity();
  const isStandardClinic = hasModule(ModuleName.AppointmentsStandard);

  useEffect(() => {
    //update component every ten seconds
    if (isSnoozed) {
      const interval = setInterval(() => {
        setCounter((count) => count + 1);
      }, 10000);
      return (): void => clearInterval(interval);
    }
  }, [isSnoozed, count]);

  const onPageRedirect = (pageName: string): void => {
    popoverRef.current?.hide();
    history.push(`${RouteDefinitions.Settings}/user/${pageName}`);
  };

  return (
    <Popover anchor={anchor} ref={popoverRef}>
      <Box w="200px" h="auto" pb={2}>
        <HStack h={16} px={4}>
          <Avatar
            border="none"
            showTooltip={false}
            size="lg"
            name={`${clinicName}`}
            src={clinicLogo || ''}
            _hover={{
              border: 'none',
              background: 'none',
            }}
          />
          <Text
            isTruncated
            noOfLines={2}
            fontWeight="bold"
            lineHeight="120%"
            _hover={{
              cursor: 'default',
            }}
          >
            {clinicName || ''}
          </Text>
        </HStack>
        {clinicOptions.length > 1 && (
          <ChangeClinicPopover
            onKeepPopoverOpen={(): void => popoverRef.current?.show()}
            clinicOptions={clinicOptions}
            onClinicSelected={onClinicSelected}
            anchor={
              <Flex
                data-testid="kibble-button-change-clinic-popover"
                height="36px"
                p="0px 20px"
                width="100%"
                cursor="pointer"
                _hover={{
                  bg: 'background.primaryAlpha.hover',
                }}
              >
                <HStack justifyContent="space-between" w="100%">
                  <Text size="sm">Change Clinic</Text>
                  <Icon name="chevronRight" size="sm" />
                </HStack>
              </Flex>
            }
          />
        )}
        <Divider pt="9px" />
        <HStack padding="12px 16px 8px 16px" spacing={3}>
          <Avatar showTooltip={false} size="lg" name={`${firstName} ${lastName}`} src={profilePic || ''} />
          <VStack spacing={0} _hover={{ cursor: 'default' }}>
            <Text
              isTruncated
              noOfLines={2}
              fontWeight="bold"
              lineHeight="120%"
              _hover={{
                cursor: 'default',
              }}
            >
              {[firstName, lastName].join(' ').trim()}
            </Text>
            <Text w="100%">{isSnoozed ? 'Inactive' : 'Active'}</Text>
          </VStack>
        </HStack>
        <UserProfilePopoverContent
          dataTestId="my-profile-button"
          mixPanelName="Profile Button"
          optionText="My Profile"
          onClick={(): void => {
            trackEvent(GeneralEvents.ViewUserProfile);
            onPageRedirect('profile');
          }}
        />
        <HasModule moduleToCheck={ModuleName.AppointmentsStandard}>
          <UserProfilePopoverContent
            dataTestId="my-notification-button"
            mixPanelName="My Notification"
            optionText="My Notifications"
            onClick={(): void => {
              trackEvent(GeneralEvents.ViewMyNotificationSettings);
              onPageRedirect('notifications');
            }}
          />
          <SnoozePopover
            anchor={
              <Flex
                data-testid="kibble-button-snooze-popover"
                p="0px 20px"
                height="36px"
                width="100%"
                cursor="pointer"
                _hover={{
                  bg: 'background.primaryAlpha.hover',
                }}
                onClick={(): void => {
                  trackEvent(GeneralEvents.SnoozeNotifications);
                }}
              >
                <HStack justifyContent="space-between" w="100%">
                  <Text size="sm">Snooze</Text>
                  <Icon name="chevronRight" size="sm" />
                </HStack>
              </Flex>
            }
          />
        </HasModule>
        {!isImpersonating && (
          <UserProfilePopoverContent
            mixPanelName="Get Help Button"
            dataTestId="get-help-button"
            optionText="Help"
            onClick={(): void => {
              if (isStandardClinic) {
                window?.zE?.('messenger', 'open');
              } else {
                openIntercomMessenger();
              }
            }}
          />
        )}
        <UserProfilePopoverContent
          mixPanelName="Log out"
          dataTestId="log-out-button"
          optionText="Log Out"
          onClick={(): Promise<void> => logout()}
        />
      </Box>
    </Popover>
  );
};

export default UserProfilePopover;
